// Migration script for Research Lines
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocs, collection, query, addDoc, orderBy, doc, setDoc, where } from 'firebase/firestore/lite';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './../firebase';
import LINEAS_ACTUALES from '../Json/LINEAS_ACTUALES.json';
import { researchLinesStaticContent } from '../data/researchLinesStaticContent';

export const MigrateResearchLines = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });

  const goToDashboard = async () => {
    navigate('/dashboard');
  };

  // Mapeo de iconos PNG a SVG para estudiantes
  const iconMap = {
    'AlmacenamientoEnergiaW.png': 'AlmacenamientoEnergia.svg',
    'ConversionEnergiaW.png': 'MusculosArtificiales.svg',
    'NanomaterialesW.png': 'Nanomateriales.svg',
    'path11826.png': 'BiosensoresElectro2.svg',
    'MusculosArtificialesW.png': 'MusculosArtificiales.svg',
    'BioElectrónica.png': 'BioElectrónica.svg',
    'software.png': 'software.svg',
    'mecatronicaR.png': 'BioElectrónica.svg',
    'logotejidosR1.png': 'logotejidosR.svg'
  };

  // Mapeo de rutas de imagen principal (del JSON) a require estático para subir a Storage
  const mainImageMap = {
    'infografias/Almacenamiento de energía.png': require('../assets/infografias/Almacenamiento de energía.png'),
    'infografias/Conversión de energía.png': require('../assets/infografias/Conversión de energía.png'),
    'infografias/NanocapsulasFarmacos.png': require('../assets/infografias/NanocapsulasFarmacos.png'),
    'infografias/BiosensoresElectroquimicos.png': require('../assets/infografias/BiosensoresElectroquimicos.png'),
    'infografias/Bioelectronica.png': require('../assets/infografias/Bioelectronica.png'),
    'infografias/Software.png': require('../assets/infografias/Software.png'),
    'infografias/Tejidos.png': require('../assets/infografias/Tejidos.png'),
    'Actuadores_electroquimicos.png': require('../assets/Actuadores_electroquimicos.png'),
    'not_found.jpg': require('../assets/not_found.jpg')
  };

  const uploadMainImageToStorage = async (imagePath) => {
    try {
      const imageModule = mainImageMap[imagePath];
      if (!imageModule) {
        console.warn(`Imagen principal ${imagePath} no encontrada en el mapeo`);
        return null;
      }
      const imageUrl = typeof imageModule === 'string' ? imageModule : imageModule.default || imageModule;
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(`Error al cargar la imagen: ${response.statusText}`);
      const blob = await response.blob();
      const fileName = imagePath.includes('/') ? imagePath.split('/').pop() : imagePath;
      const storageRef = ref(storage, `research-lines-images/${fileName}`);
      await uploadBytes(storageRef, blob);
      return getDownloadURL(storageRef);
    } catch (error) {
      console.error(`Error al subir imagen principal ${imagePath}:`, error);
      return null;
    }
  };

  // Función para subir un icono a Firebase Storage
  const uploadIconToStorage = async (iconName) => {
    try {
      // Intentar cargar el icono desde el repositorio usando require estático
      // Necesitamos importar todos los iconos posibles o usar una ruta pública
      let iconModule;
      
      // Mapeo de iconos conocidos para usar require estático
      const iconMap = {
        'AlmacenamientoEnergiaW.png': require('../assets/icons-lineas/AlmacenamientoEnergiaW.png'),
        'ConversionEnergiaW.png': require('../assets/icons-lineas/ConversionEnergiaW.png'),
        'NanomaterialesW.png': require('../assets/icons-lineas/NanomaterialesW.png'),
        'path11826.png': require('../assets/icons-lineas/path11826.png'),
        'MusculosArtificialesW.png': require('../assets/icons-lineas/MusculosArtificialesW.png'),
        'BioElectrónica.png': require('../assets/icons-lineas/BioElectronica.png'),
        'BioElectronica.png': require('../assets/icons-lineas/BioElectronica.png'),
        'software.png': require('../assets/icons-lineas/software.png'),
        'mecatronicaR.png': require('../assets/icons-lineas/mecatronicaR.png'),
        'logotejidosR1.png': require('../assets/icons-lineas/logotejidosR1.png')
      };

      if (!iconMap[iconName]) {
        console.warn(`Icono ${iconName} no encontrado en el mapeo`);
        return null;
      }

      iconModule = iconMap[iconName];
      
      // Convertir la ruta del módulo a URL pública
      const iconUrl = typeof iconModule === 'string' ? iconModule : iconModule.default || iconModule;

      // Obtener el archivo como blob usando fetch
      const response = await fetch(iconUrl);
      if (!response.ok) {
        throw new Error(`Error al cargar el icono: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Crear referencia en Firebase Storage
      const storageRef = ref(storage, `research-lines-icons/${iconName}`);
      
      // Subir el archivo
      await uploadBytes(storageRef, blob);
      
      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error(`Error al subir el icono ${iconName}:`, error);
      return null;
    }
  };

  const migrateResearchLines = async () => {
    setIsRunning(true);
    setStatus('Iniciando migración...');
    setProgress({ current: 0, total: LINEAS_ACTUALES.length, message: 'Migrando líneas de investigación' });

    try {
      // Paso 0: Subir iconos a Firebase Storage
      setStatus(prev => prev + '\nSubiendo iconos a Firebase Storage...');
      const totalIcons = LINEAS_ACTUALES.filter(linea => linea.icon).length;
      setProgress({ current: 0, total: totalIcons, message: 'Subiendo iconos' });
      
      const iconUrls = {};
      let iconIndex = 0;
      
      for (const linea of LINEAS_ACTUALES) {
        if (linea.icon && !iconUrls[linea.icon]) {
          setProgress({
            current: iconIndex + 1,
            total: totalIcons,
            message: `Subiendo icono: ${linea.icon}`
          });
          
          const iconUrl = await uploadIconToStorage(linea.icon);
          if (iconUrl) {
            iconUrls[linea.icon] = iconUrl;
            setStatus(prev => prev + `\n✓ Icono subido: ${linea.icon}`);
          } else {
            setStatus(prev => prev + `\n✗ Error al subir icono: ${linea.icon}`);
          }
          iconIndex++;
        }
      }

      // Paso 0.5: Subir imágenes principales (infografías) a Firebase Storage
      setStatus(prev => prev + '\nSubiendo imágenes principales a Firebase Storage...');
      const linesWithImage = LINEAS_ACTUALES.filter(linea => linea.image);
      const mainImageUrls = {};
      for (let idx = 0; idx < linesWithImage.length; idx++) {
        const linea = linesWithImage[idx];
        if (linea.image && !mainImageUrls[linea.image]) {
          setProgress({
            current: idx + 1,
            total: linesWithImage.length,
            message: `Subiendo imagen principal: ${linea.image}`
          });
          const url = await uploadMainImageToStorage(linea.image);
          if (url) {
            mainImageUrls[linea.image] = url;
            setStatus(prev => prev + `\n✓ Imagen principal subida: ${linea.image}`);
          } else {
            setStatus(prev => prev + `\n✗ Error al subir imagen: ${linea.image}`);
          }
        }
      }

      // Paso 1: Verificar y migrar líneas de investigación
      setStatus(prev => prev + '\nMigrando líneas de investigación...');
      const existingLinesQuery = query(collection(db, 'research-lines'), orderBy('order'));
      const existingLinesSnapshot = await getDocs(existingLinesQuery);
      const existingLines = existingLinesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      // Crear un mapa de IDs de líneas para referencia rápida
      const lineIdMap = {}; // Mapa de nombre -> id para usar en sub-líneas

      let linesCreated = 0;
      let linesUpdated = 0;

      for (let i = 0; i < LINEAS_ACTUALES.length; i++) {
        const linea = LINEAS_ACTUALES[i];
        setProgress({ 
          current: i + 1, 
          total: LINEAS_ACTUALES.length, 
          message: `Procesando: ${linea.name}` 
        });

        // Buscar si ya existe una línea: primero por nombre, luego por ID (si el JSON tiene ID)
        let existingLine = existingLines.find(l => l.name === linea.name);
        
        // Si no se encuentra por nombre y el JSON tiene un ID, buscar por ID
        if (!existingLine && linea.id) {
          existingLine = existingLines.find(l => l.id === linea.id);
        }

        // Imagen principal: preferir la subida (infografía), sino icono subido, sino la existente en Firebase
        const imageUrl = mainImageUrls[linea.image] || iconUrls[linea.icon] || existingLine?.image || null;

        // Contenido: preferir el estático (párrafos por nombre), sino el del JSON (convertir strings a bloques si hace falta)
        let content = researchLinesStaticContent[linea.name] || linea.content || [];
        if (Array.isArray(content) && content.length > 0 && typeof content[0] === 'string') {
          content = content.map((t, id) => ({ type: 'paragraph', id: Date.now() + id, text: t, content: t }));
        }

        const lineData = {
          name: linea.name,
          icon: linea.icon,
          color: linea.color,
          image: imageUrl,
          order: linea.order || i,
          enabled: true,
          content,
          createdDate: existingLine?.createdDate || new Date(),
          updatedDate: new Date()
        };

        let lineId;
        if (existingLine) {
          // Actualizar línea existente
          const lineRef = doc(db, 'research-lines', existingLine.id);
          await setDoc(lineRef, lineData, { merge: true });
          lineId = existingLine.id;
          linesUpdated++;
        } else {
          // Crear nueva línea
          const docRef = await addDoc(collection(db, 'research-lines'), lineData);
          lineId = docRef.id;
          linesCreated++;
        }
        
        // Guardar el ID en el mapa para usar en sub-líneas
        lineIdMap[linea.name] = lineId;
      }

      setStatus(`✓ Líneas migradas: ${linesCreated} creadas, ${linesUpdated} actualizadas`);

      // Paso 2: Migrar sub-líneas de investigación
      setStatus(prev => prev + '\nMigrando sub-líneas de investigación...');
      const totalSubLines = LINEAS_ACTUALES.reduce((acc, line) => acc + (line.subLineas?.length || 0), 0);
      setProgress({ current: 0, total: totalSubLines, message: 'Migrando sub-líneas' });

      let subLinesCreated = 0;
      let subLinesUpdated = 0;
      let subLineIndex = 0;

      for (const linea of LINEAS_ACTUALES) {
        if (linea.subLineas && linea.subLineas.length > 0) {
          // Obtener el ID de la línea de investigación desde el mapa
          const lineaId = lineIdMap[linea.name];
          if (!lineaId) {
            setStatus(prev => prev + `\n⚠ Advertencia: No se encontró ID para la línea "${linea.name}", omitiendo sub-líneas`);
            continue;
          }

          for (const subLinea of linea.subLineas) {
            setProgress({
              current: subLineIndex + 1,
              total: totalSubLines,
              message: `Procesando sub-línea: ${subLinea.titulo}`
            });

            // Buscar si ya existe una sub-línea con el mismo título y línea de investigación
            // Primero buscar por ID de línea (preferido)
            let existingSubLinesQuery = query(
              collection(db, 'sub-research-lines'),
              where('titulo', '==', subLinea.titulo),
              where('linea_investigacion', '==', lineaId)
            );
            let existingSubLinesSnapshot = await getDocs(existingSubLinesQuery);
            let existingSubLine = existingSubLinesSnapshot.docs[0];

            // Si no se encuentra por ID, buscar por nombre (compatibilidad con datos antiguos)
            if (!existingSubLine) {
              existingSubLinesQuery = query(
                collection(db, 'sub-research-lines'),
                where('titulo', '==', subLinea.titulo),
                where('linea_investigacion', '==', linea.name)
              );
              existingSubLinesSnapshot = await getDocs(existingSubLinesQuery);
              existingSubLine = existingSubLinesSnapshot.docs[0];
            }

            const subLineData = {
              titulo: subLinea.titulo,
              linea_investigacion: lineaId, // Usar ID en lugar de nombre
              orden: subLinea.orden || 0,
              enabled: true,
              createdDate: existingSubLine?.data().createdDate || new Date(),
              updatedDate: new Date()
            };

            if (existingSubLine) {
              // Actualizar sub-línea existente
              await setDoc(doc(db, 'sub-research-lines', existingSubLine.id), subLineData, { merge: true });
              subLinesUpdated++;
            } else {
              // Crear nueva sub-línea
              await addDoc(collection(db, 'sub-research-lines'), subLineData);
              subLinesCreated++;
            }
            subLineIndex++;
          }
        }
      }

      setStatus(prev => prev + `\n✓ Sub-líneas migradas: ${subLinesCreated} creadas, ${subLinesUpdated} actualizadas`);

      // Paso 3: Actualizar estudiantes
      setProgress({ current: 0, total: 0, message: 'Actualizando estudiantes...' });
      const studentsQuery = query(collection(db, 'students'));
      const studentsSnapshot = await getDocs(studentsQuery);
      const students = studentsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      // Recargar las líneas existentes para obtener los IDs actualizados
      const updatedLinesQuery = query(collection(db, 'research-lines'), orderBy('order'));
      const updatedLinesSnapshot = await getDocs(updatedLinesQuery);
      const updatedLines = updatedLinesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      setProgress({ current: 0, total: students.length, message: 'Actualizando estudiantes' });

      let studentsUpdated = 0;
      let studentsSkipped = 0;

      for (let i = 0; i < students.length; i++) {
        const student = students[i];
        setProgress({ 
          current: i + 1, 
          total: students.length, 
          message: `Actualizando estudiante: ${student.name || 'Sin nombre'}` 
        });

        // Si el estudiante ya tiene una línea de investigación válida
        if (student.linea_investigacion) {
          // Buscar la línea: primero por nombre, luego por ID
          let matchingLine = LINEAS_ACTUALES.find(
            l => l.name === student.linea_investigacion
          );
          
          // Si no se encuentra por nombre, puede ser que sea un ID
          if (!matchingLine) {
            // Buscar en las líneas actualizadas por ID
            const existingLineById = updatedLines.find(
              l => l.id === student.linea_investigacion
            );
            if (existingLineById) {
              // Buscar la línea en LINEAS_ACTUALES por nombre
              matchingLine = LINEAS_ACTUALES.find(
                l => l.name === existingLineById.name
              );
            }
          }

          if (matchingLine) {
            // Obtener el ID de la línea desde el mapa o desde las líneas actualizadas
            const lineaId = lineIdMap[matchingLine.name] || 
                           updatedLines.find(l => l.name === matchingLine.name)?.id;
            
            // Actualizar el icono del estudiante basado en la línea
            const iconSvg = iconMap[matchingLine.icon] || matchingLine.icon.replace('.png', '.svg');
            
            const updateData = {
              icon_inv: iconSvg,
              updatedDate: new Date()
            };
            
            // Si encontramos un ID y el estudiante tiene el nombre, actualizar al ID
            if (lineaId && student.linea_investigacion === matchingLine.name) {
              updateData.linea_investigacion = lineaId;
            }
            
            const studentRef = doc(db, 'students', student.id);
            await setDoc(studentRef, updateData, { merge: true });
            studentsUpdated++;
          } else {
            studentsSkipped++;
          }
        } else {
          studentsSkipped++;
        }
      }

      setStatus(prev => prev + `\n✓ Estudiantes actualizados: ${studentsUpdated} actualizados, ${studentsSkipped} omitidos`);
      setProgress({ current: 0, total: 0, message: 'Migración completada exitosamente' });
      
      alert(`Migración completada exitosamente!\n\nLíneas: ${linesCreated} creadas, ${linesUpdated} actualizadas\nSub-líneas: ${subLinesCreated} creadas, ${subLinesUpdated} actualizadas\nEstudiantes: ${studentsUpdated} actualizados`);
    } catch (error) {
      console.error('Error durante la migración:', error);
      setStatus(`✗ Error: ${error.message}`);
      alert(`Error durante la migración: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container-fluid mt-1">
      <div className="card shadow">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <button className="btn btn-danger" onClick={goToDashboard}>
            Regresar
          </button>
          <h3 className="mb-0">Migración de Líneas de Investigación</h3>
        </div>
        <div className="card-body">
          <div className="alert alert-warning">
            <strong>Advertencia:</strong> Este script migrará las líneas de investigación y sub-líneas desde el JSON a Firebase
            y actualizará los iconos de los estudiantes según su línea de investigación actual.
            <br />
            <strong>Líneas a migrar:</strong> {LINEAS_ACTUALES.length}
            <br />
            <strong>Sub-líneas a migrar:</strong> {LINEAS_ACTUALES.reduce((acc, line) => acc + (line.subLineas?.length || 0), 0)}
          </div>

          {isRunning && (
            <div className="alert alert-info">
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <div>
                  <strong>Procesando...</strong>
                  {progress.total > 0 && (
                    <div>
                      {progress.current} de {progress.total} - {progress.message}
                    </div>
                  )}
                  {progress.message && progress.total === 0 && (
                    <div>{progress.message}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {status && (
            <div className="alert alert-secondary">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{status}</pre>
            </div>
          )}

          <button
            className="btn btn-primary btn-lg"
            onClick={migrateResearchLines}
            disabled={isRunning}
          >
            {isRunning ? 'Migrando...' : 'Iniciar Migración'}
          </button>

          <div className="mt-4">
            <h5>Líneas que se migrarán:</h5>
            <ul className="list-group">
              {LINEAS_ACTUALES.map((linea, index) => (
                <li key={index} className="list-group-item">
                  <strong>{linea.name}</strong>
                  <br />
                  <small>
                    Icono: {linea.icon} | Color: {linea.color} | 
                    Sub-líneas: {linea.subLineas?.length || 0}
                  </small>
                  {linea.subLineas && linea.subLineas.length > 0 && (
                    <ul className="mt-2 mb-0">
                      {linea.subLineas.map((subLinea, subIndex) => (
                        <li key={subIndex} style={{fontSize: '0.85rem'}}>
                          {subLinea.titulo} (orden: {subLinea.orden || 0})
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

